import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { EmptyState, useAccountingPermissions } from "../shared";
import { ACCOUNTING_BASE } from "../accountingNav";
import { downloadReportCsv } from "./reportShared";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Colors lifted straight from the Society's own reference spreadsheet:
// dark navy title banner, muted blue-gray section header bands, and a light
// gray fill across every data cell in the used range - same palette on
// screen and on print, not just a plain app-style table.
const TITLE_BG = "#1F3864";
const HEADER_BG = "#A9BCD0";
const BODY_BG = "#D9D9D9";
const BORDER = "#9CA3AF";
const NAVY_TEXT = "#1F3864";

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const cellBase = { border: `1px solid ${BORDER}`, padding: "3px 8px", background: BODY_BG };
const blankCell = { padding: "3px 8px", background: BODY_BG };
const headerCell = { ...cellBase, background: HEADER_BG, fontWeight: 700, color: "#111827" };
const amountCell = (bold) => ({ ...cellBase, textAlign: "right", color: NAVY_TEXT, fontWeight: bold ? 700 : 400, fontVariantNumeric: "tabular-nums" });
const labelCell = (bold) => ({ ...cellBase, fontWeight: bold ? 700 : 400, color: "#111827" });

/**
 * Renders the report as one literal spreadsheet-style grid (5 columns, A-E,
 * matching the Society's reference file column-for-column) - title banner,
 * Particulars/Income/Expenditure with Surplus, Liabilities&Funds-vs-Assets,
 * Account Opening/Receipts/Payments/Closing, Summary, and signature blocks.
 * Rendered identically on screen and on print - same colors, same cell
 * positions - via one shared component instead of two diverging layouts.
 */
function SpreadsheetReport({ data, onOpenLedger, onOpenReceivables, onOpenPayables }) {
  const ie = data.incomeExpenditure;
  const bs = data.balanceSheet;
  const ieRows = [
    ...ie.income.map((r) => ({ ...r, side: "income" })),
    ...ie.expenditure.map((r) => ({ ...r, side: "expenditure" })),
  ];
  const bsRowCount = Math.max(bs.liabilities.length, bs.assets.length);

  const Name = ({ ledgerId, children }) =>
    ledgerId ? (
      <button type="button" onClick={() => onOpenLedger(ledgerId)} style={{ color: "inherit", font: "inherit", textAlign: "left", textDecoration: "underline dotted" }}>
        {children}
      </button>
    ) : (
      <>{children}</>
    );

  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", tableLayout: "fixed" }}>
      <colgroup>
        <col style={{ width: "30%" }} /><col style={{ width: "17.5%" }} /><col style={{ width: "17.5%" }} /><col style={{ width: "17.5%" }} /><col style={{ width: "17.5%" }} />
      </colgroup>
      <tbody>
        {/* Letterhead - Society Name + full registered address, read from
            the same client profile Billing already uses (organization_name/
            address/city/state/pincode), so this can never drift out of sync
            with the Society's own master data. */}
        {data.society?.name && (
          <tr>
            <td colSpan={5} style={{ background: TITLE_BG, color: "#fff", fontWeight: 800, textAlign: "center", padding: "6px 8px 0", letterSpacing: "0.02em", fontSize: "14px" }}>
              {data.society.name.toUpperCase()}
            </td>
          </tr>
        )}
        {data.society?.address && (
          <tr>
            <td colSpan={5} style={{ background: TITLE_BG, color: "#E5E7EB", fontWeight: 400, textAlign: "center", padding: "0 8px 6px", fontSize: "10px" }}>
              {data.society.address}
            </td>
          </tr>
        )}
        {/* Title banner */}
        <tr>
          <td colSpan={5} style={{ background: TITLE_BG, color: "#fff", fontWeight: 800, textAlign: "center", padding: "8px", letterSpacing: "0.02em", borderTop: data.society?.name ? "1px solid #4C6584" : "none" }}>
            MONTHLY SOCIETY BALANCE SHEET — {data.monthLabel.toUpperCase()}
          </td>
        </tr>
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>

        {/* Income & Expenditure */}
        <tr>
          <td style={headerCell}>Particulars</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Income (₹)</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Expenditure (₹)</td>
          <td style={blankCell} /><td style={blankCell} />
        </tr>
        {ieRows.map((r, i) => (
          <tr key={i}>
            <td style={labelCell(false)}><Name ledgerId={r.ledgerId}>{r.ledgerName}</Name></td>
            <td style={amountCell(false)}>{r.side === "income" ? fmt(r.amount) : ""}</td>
            <td style={amountCell(false)}>{r.side === "expenditure" ? fmt(r.amount) : ""}</td>
            <td style={blankCell} /><td style={blankCell} />
          </tr>
        ))}
        <tr>
          <td style={labelCell(true)}>TOTAL</td>
          <td style={amountCell(true)}>{fmt(ie.totalIncome)}</td>
          <td style={amountCell(true)}>{fmt(ie.totalExpenditure)}</td>
          <td style={blankCell} /><td style={blankCell} />
        </tr>
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>
        <tr>
          <td style={labelCell(true)}>{ie.isDeficit ? "DEFICIT FOR THE MONTH" : "SURPLUS FOR THE MONTH"}</td>
          <td style={amountCell(true)}>{fmt(ie.isDeficit ? ie.deficit : ie.surplus)}</td>
          <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
        </tr>
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>

        {/* Liabilities/Funds vs Assets */}
        <tr>
          <td style={headerCell}>Liabilities / Funds</td>
          <td style={{ ...headerCell, textAlign: "right" }}>₹</td>
          <td style={headerCell}>Assets</td>
          <td style={{ ...headerCell, textAlign: "right" }}>₹</td>
          <td style={blankCell} />
        </tr>
        {Array.from({ length: bsRowCount }).map((_, i) => {
          const l = bs.liabilities[i];
          const a = bs.assets[i];
          return (
            <tr key={i}>
              <td style={labelCell(false)}>{l ? <Name ledgerId={l.ledgerId}>{l.ledgerName}</Name> : ""}</td>
              <td style={amountCell(false)}>{l ? fmt(l.balance) : ""}</td>
              <td style={labelCell(false)}>{a ? <Name ledgerId={a.ledgerId}>{a.ledgerName}</Name> : ""}</td>
              <td style={amountCell(false)}>{a ? fmt(a.balance) : ""}</td>
              <td style={blankCell} />
            </tr>
          );
        })}
        <tr>
          <td style={labelCell(true)}>TOTAL</td>
          <td style={amountCell(true)}>{fmt(bs.totalLiabilities)}</td>
          <td style={labelCell(true)}>TOTAL</td>
          <td style={amountCell(true)}>{fmt(bs.totalAssets)}</td>
          <td style={blankCell} />
        </tr>
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>

        {/* Bank & Cash Account */}
        <tr>
          <td style={headerCell}>Account</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Opening</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Receipts</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Payments</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Closing</td>
        </tr>
        {data.bankCash.map((r) => (
          <tr key={r.ledgerId}>
            <td style={labelCell(false)}><Name ledgerId={r.ledgerId}>{r.ledgerName}</Name></td>
            <td style={amountCell(false)}>{fmt(r.opening)}</td>
            <td style={amountCell(false)}>{fmt(r.receipts)}</td>
            <td style={amountCell(false)}>{fmt(r.payments)}</td>
            <td style={amountCell(false)}>{fmt(r.closing)}</td>
          </tr>
        ))}
        {data.bankCash.length === 0 && (
          <tr><td colSpan={5} style={{ ...cellBase, textAlign: "center", color: "#374151" }}>No Bank/Cash activity or balance this month.</td></tr>
        )}
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>

        {/* Summary */}
        <tr>
          <td style={headerCell}>Summary</td>
          <td style={{ ...headerCell, textAlign: "right" }}>Amount (₹)</td>
          <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
        </tr>
        <tr>
          <td style={labelCell(false)}>
            {onOpenReceivables ? (
              <button type="button" onClick={onOpenReceivables} style={{ color: "inherit", font: "inherit", textAlign: "left", textDecoration: "underline dotted" }}>Member Outstanding</button>
            ) : "Member Outstanding"}
          </td>
          <td style={amountCell(false)}>{fmt(data.summary.memberOutstanding)}</td>
          <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
        </tr>
        <tr>
          <td style={labelCell(false)}>Accounting Member Receivables</td>
          <td style={amountCell(false)}>{fmt(data.summary.accountingMemberReceivables)}</td>
          <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
        </tr>
        {Math.abs(Number(data.summary.memberOutstandingDifference || 0)) > 0.01 && (
          <tr>
            <td style={{ ...labelCell(true), color: "#B91C1C" }}>Billing / Accounting Difference</td>
            <td style={{ ...amountCell(true), color: "#B91C1C" }}>{fmt(data.summary.memberOutstandingDifference)}</td>
            <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
          </tr>
        )}
        <tr>
          <td style={labelCell(false)}>Member Advances</td>
          <td style={amountCell(false)}>{fmt(data.summary.memberAdvances)}</td>
          <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
        </tr>
        <tr>
          <td style={labelCell(false)}>
            {onOpenPayables ? (
              <button type="button" onClick={onOpenPayables} style={{ color: "inherit", font: "inherit", textAlign: "left", textDecoration: "underline dotted" }}>Vendor Payables</button>
            ) : "Vendor Payables"}
          </td>
          <td style={amountCell(false)}>{fmt(data.summary.vendorPayables)}</td>
          <td style={blankCell} /><td style={blankCell} /><td style={blankCell} />
        </tr>
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>

        {/* Signatures - a nested 3-equal-column table spanning the full
            width of the report (not tied to the 30/17.5/17.5/17.5/17.5
            outer column widths, which would leave Chairperson short and
            two columns blank on the right). */}
        <tr>
          <td colSpan={5} style={{ padding: 0 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px", tableLayout: "fixed" }}>
              <colgroup><col style={{ width: "33.34%" }} /><col style={{ width: "33.33%" }} /><col style={{ width: "33.33%" }} /></colgroup>
              <tbody>
                <tr>
                  <td style={headerCell}>Treasurer</td>
                  <td style={headerCell}>Secretary</td>
                  <td style={headerCell}>Chairperson</td>
                </tr>
                <tr>
                  <td style={labelCell(false)}>Signature / Date</td>
                  <td style={labelCell(false)}>Signature / Date</td>
                  <td style={labelCell(false)}>Signature / Date</td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
        <tr><td colSpan={5} style={{ ...blankCell, height: 8 }} /></tr>
        <tr>
          <td colSpan={5} style={{ ...blankCell, fontStyle: "italic", fontSize: "10px", color: "#374151" }}>
            Generated from posted Accounting entries — {new Date().toLocaleString("en-IN")}.
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/**
 * Phase 17 addendum - MONTHLY COMMITTEE ACCOUNTS, rendered as a literal
 * spreadsheet-style report (see SpreadsheetReport above) matching the
 * Society's own reference file layout and colors exactly - not a generic
 * app-style card/table. Every figure comes from the backend's condensed view
 * over the same report functions the detailed yearly reports use (see
 * report_monthly_committee_accounts).
 */
export default function MonthlyCommitteeAccountsPage() {
  const navigate = useNavigate();
  const perms = useAccountingPermissions();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);

  const params = { year, month };

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/society/accounting/reports/monthly-committee-accounts", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatErr(err, "Could not load monthly committee accounts"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, month]);

  const fetchPdf = async () => {
    const res = await api.get("/society/accounting/reports/monthly-committee-accounts", {
        params: { ...params, format: "pdf" }, responseType: "blob",
    });
    const contentType = String(res.headers?.["content-type"] || res.data?.type || "").toLowerCase();
    if (!contentType.includes("application/pdf")) {
      throw new Error("The server did not return a PDF. Please redeploy the backend and try again.");
    }
    return res.data;
  };

  const downloadPdf = async () => {
    try {
      setPdfLoading(true);
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `monthly-financial-summary-${year}-${String(month).padStart(2, "0")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error(formatErr(err, "Could not download monthly financial summary PDF"));
    } finally {
      setPdfLoading(false);
    }
  };

  const printPdf = async () => {
    // Open the tab immediately so browsers do not block it after the async request.
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow pop-ups to print the formatted PDF.");
      return;
    }
    try {
      setPrintLoading(true);
      printWindow.document.write("<title>Preparing monthly financial summary...</title><p style='font-family:sans-serif;padding:24px'>Preparing formatted PDF...</p>");
      const blob = await fetchPdf();
      const url = URL.createObjectURL(blob);
      printWindow.location.replace(url);
      // Keep the Blob URL alive while Chrome's PDF viewer loads and prints it.
      setTimeout(() => {
        try { printWindow.focus(); printWindow.print(); } catch (_) { /* PDF remains open for manual printing. */ }
      }, 1500);
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (err) {
      printWindow.close();
      toast.error(formatErr(err, "Could not prepare the monthly financial summary for printing"));
    } finally {
      setPrintLoading(false);
    }
  };

  const openLedger = (ledgerId) => ledgerId && navigate(`${ACCOUNTING_BASE}/reports/general-ledger?ledgerId=${ledgerId}&fromDate=${data?.fromDate || ""}&toDate=${data?.toDate || ""}`);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Monthly Financial Summary</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Easy-to-read monthly income, expenses, bank/cash movement, balances and committee approval report.</p>
      </div>

      <div className="report-screen-only space-y-4">
        <div className="flex flex-wrap items-end gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4">
          <div className="w-44 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Month</div>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((label, i) => <SelectItem key={i + 1} value={String(i + 1)}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-32 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Year</div>
            <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || now.getFullYear())} />
          </div>
          {perms.canPrint && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" disabled={pdfLoading || loading || !data} onClick={downloadPdf}>
                <Download className="mr-2 h-4 w-4" /> {pdfLoading ? "Preparing PDF..." : "Download PDF"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadReportCsv("/society/accounting/reports/monthly-committee-accounts", params, `monthly-society-balance-sheet-${year}-${String(month).padStart(2, "0")}.csv`)}>
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
              <Button variant="outline" size="sm" disabled={printLoading || pdfLoading || loading || !data} onClick={printPdf}>
                <Printer className="mr-2 h-4 w-4" /> {printLoading ? "Preparing Print..." : "Print PDF"}
              </Button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : !data ? (
          <EmptyState>No data.</EmptyState>
        ) : (
          <div style={{ border: `2px solid ${TITLE_BG}`, maxWidth: 1000, margin: "0 auto" }}>
            <SpreadsheetReport
              data={data} onOpenLedger={openLedger}
              onOpenReceivables={() => navigate(`${ACCOUNTING_BASE}/reports/receivables`)}
              onOpenPayables={() => navigate(`${ACCOUNTING_BASE}/reports/payables`)}
            />
          </div>
        )}
      </div>

      {data && (
        <div className="report-print-only">
          <div id="report-print-area" className="report-print-document mx-auto max-w-[1000px] bg-white p-2">
            <div style={{ border: `2px solid ${TITLE_BG}` }}>
              <SpreadsheetReport data={data} onOpenLedger={() => {}} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
